import { useEffect, useRef } from "react";

const modalStack = [];
let originalOverflow = "";
const focusable = panel => [...panel.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])')]
  .filter(element => element.getClientRects().length && !element.closest('[inert]'));

export function useDialogBehavior(open, onClose, closeOnEsc = true) {
  const panelRef = useRef(null);
  const closeRef = useRef(onClose);
  const escRef = useRef(closeOnEsc);
  closeRef.current = onClose;
  escRef.current = closeOnEsc;
  useEffect(() => {
    if (!open) return;
    const ticket = Symbol("modal");
    const previousFocus = document.activeElement;
    const panel = panelRef.current;
    if (!panel) return;
    if (!modalStack.length) originalOverflow = document.body.style.overflow;
    modalStack.push(ticket);
    document.body.style.overflow = "hidden";
    const isTop = () => modalStack.at(-1) === ticket;
    const focusFirst = () => (focusable(panel)[0] || panel).focus();
    focusFirst();
    const onKey = event => {
      if (!isTop() || event.defaultPrevented) return;
      if (event.key === "Escape") {
        event.stopPropagation();
        if (escRef.current && closeRef.current) { event.preventDefault(); closeRef.current(); }
      }
      if (event.key !== "Tab") return;
      const items = focusable(panel), first = items[0], last = items.at(-1);
      if (!first) { event.preventDefault(); panel.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    const onFocus = event => { if (isTop() && !panel.contains(event.target)) focusFirst(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("focusin", onFocus);
    return () => {
      const wasTop = isTop();
      modalStack.splice(modalStack.indexOf(ticket), 1);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("focusin", onFocus);
      if (!modalStack.length) document.body.style.overflow = originalOverflow;
      if (wasTop && previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);

  return panelRef;
}
